/**
 * A lista de Casos da equipe por pasta (owner, 04/11), no desenho da do portal: uma linha por pasta
 * (o SBR do cliente) com o processo, o titular, quantos casos e CCBs, a situação resumida e o prazo
 * mais próximo. O nome da pasta é um botão que abre, logo abaixo, a tabela de sempre com os casos
 * dela (cada linha com as ações e o acesso ao caso).
 *
 * Com a busca ou o filtro ativos, as pastas já vêm abertas; com uma pasta só, também. Cada clique
 * inverte a pasta; trocar a busca ou o filtro remonta a lista (a `key` na página) e volta ao padrão.
 */
import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import {
  Badge,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';

import { marcaDoPrazo } from './ordemDaLista';
import type { PastaDaEquipe } from './pastasDaLista';
import { formatDeadline } from './url_state';

const t = ptBR.casos.pastas;
const COLUNAS_DA_PASTA = 6;

function contagem(n: number, um: string, varios: string): string {
  return n === 1 ? um : varios.replace('{n}', String(n));
}

/** "3 pastas · 7 casos" — a linha acima da lista. */
export function contagemDasPastas(pastas: number, casos: number): string {
  return [contagem(pastas, t.pastaUma, t.pastas), contagem(casos, t.casoUm, t.casos)].join(' · ');
}

function titular(p: PastaDaEquipe): string {
  const [primeiro, ...outros] = p.titulares;
  if (primeiro === undefined) return t.naoInformado;
  if (outros.length === 0) return primeiro;
  return t.titularesMais.replace('{titular}', primeiro).replace('{n}', String(outros.length));
}

function PrazoDaPasta({ caso }: { caso: CaseRecordData | null }) {
  if (caso === null) return <>{ptBR.casos.semData}</>;
  const marca = marcaDoPrazo(caso, new Date());
  return (
    <>
      {formatDeadline(caso.deadline)}
      {marca !== null ? (
        <Badge variant={marca.tom} className="ml-2 align-middle font-sans">
          {marca.texto}
        </Badge>
      ) : null}
    </>
  );
}

/** A tabela de sempre, com os casos de uma pasta. */
function CasosDaPasta({ children }: { children: ReactNode }) {
  const c = ptBR.casos;
  return (
    <Table className="min-w-[56rem] text-left">
      <TableHeader>
        <TableRow>
          <TableHead>{c.tableColumnNumero}</TableHead>
          <TableHead>{c.tableColumnCaso}</TableHead>
          <TableHead>{c.tableColumnEspecime}</TableHead>
          <TableHead>{c.tableColumnLr}</TableHead>
          <TableHead>{c.tableColumnStatus}</TableHead>
          <TableHead>{c.tableColumnPrazo}</TableHead>
          <TableHead data-testid="th-col-received-at">{c.tableColumnAssinatura}</TableHead>
          <TableHead data-testid="th-col-created-at">{c.tableColumnCadastro}</TableHead>
          <TableHead className="text-right">{c.table.columnActions}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

function LinhaDaPasta({
  pasta,
  aberta,
  onAlternar,
  renderCaso,
}: {
  pasta: PastaDaEquipe;
  aberta: boolean;
  onAlternar: () => void;
  renderCaso: (caso: CaseRecordData) => ReactNode;
}) {
  const casosId = useId();
  const nome = pasta.pasta ?? t.semPasta;
  const Seta = aberta ? ChevronDown : ChevronRight;
  return (
    <>
      <TableRow data-testid="pastas-linha" data-aberta={aberta}>
        <TableCell className="min-w-[190px] align-top">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberta}
            aria-controls={casosId}
            data-testid="pastas-abrir"
            className="inline-flex items-start gap-1 text-left font-semibold text-fg underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Seta aria-hidden="true" className="mt-[3px] h-4 w-4 shrink-0" />
            {nome}
          </button>
          {pasta.prioritaria ? (
            <Badge variant="warning" className="ml-2 align-middle">
              {ptBR.casos.table.prioritarioBadge}
            </Badge>
          ) : null}
        </TableCell>
        <TableCell className="align-top font-mono text-caption text-fg-muted">
          {pasta.processos.length > 0 ? pasta.processos.join(' · ') : t.naoInformado}
        </TableCell>
        <TableCell className="align-top text-fg">{titular(pasta)}</TableCell>
        <TableCell className="align-top" data-testid="pastas-contagem">
          {contagem(pasta.casos.length, t.casoUm, t.casos)}
          {pasta.ccbs > 0 ? (
            <div className="text-caption text-fg-muted">
              {contagem(pasta.ccbs, t.ccbUma, t.ccbs)}
            </div>
          ) : null}
        </TableCell>
        <TableCell className="align-top">
          <span className="flex flex-wrap gap-1">
            {pasta.situacoes.map((s) => (
              <StatusPill
                key={s.label}
                status={s.status}
                label={t.situacaoNaPasta.replace('{situacao}', s.label).replace('{n}', String(s.n))}
              />
            ))}
          </span>
        </TableCell>
        <TableCell className="align-top font-mono text-caption text-fg-muted">
          <PrazoDaPasta caso={pasta.casoDoPrazo} />
        </TableCell>
      </TableRow>
      {aberta ? (
        <TableRow data-testid="pastas-casos" className="hover:bg-transparent">
          <TableCell colSpan={COLUNAS_DA_PASTA} className="bg-fg/[0.03] px-2 py-1">
            <div
              id={casosId}
              role="region"
              aria-label={t.casosDaPasta.replace('{pasta}', nome)}
              className="overflow-x-auto"
            >
              <CasosDaPasta>{pasta.casos.map(renderCaso)}</CasosDaPasta>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export function PastasDaEquipe({
  pastas,
  abertasPorPadrao,
  renderCaso,
}: {
  pastas: PastaDaEquipe[];
  /** A busca ou o filtro ativos, ou uma pasta só: as pastas vêm abertas. */
  abertasPorPadrao: boolean;
  renderCaso: (caso: CaseRecordData) => ReactNode;
}) {
  const [invertidas, setInvertidas] = useState<ReadonlySet<string>>(new Set());

  function alternar(chave: string): void {
    setInvertidas((atuais) => {
      const novas = new Set(atuais);
      if (novas.has(chave)) novas.delete(chave);
      else novas.add(chave);
      return novas;
    });
  }

  return (
    <Table className="min-w-[56rem] text-left" data-testid="pastas-da-equipe">
      <TableHeader>
        <TableRow>
          <TableHead>{t.colPasta}</TableHead>
          <TableHead>{t.colProcesso}</TableHead>
          <TableHead>{t.colTitular}</TableHead>
          <TableHead>{t.colCasos}</TableHead>
          <TableHead>{t.colSituacao}</TableHead>
          <TableHead>{t.colPrazo}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pastas.map((p) => (
          <LinhaDaPasta
            key={p.chave}
            pasta={p}
            aberta={abertasPorPadrao !== invertidas.has(p.chave)}
            onAlternar={() => alternar(p.chave)}
            renderCaso={renderCaso}
          />
        ))}
      </TableBody>
    </Table>
  );
}
